import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Group } from '@visx/group';
import { scaleTime, scaleLinear } from '@visx/scale';
import { AxisLeft, AxisBottom, TickFormatter } from '@visx/axis';
import { LinePath } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { Tooltip, useTooltip } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { Text } from '@visx/text';
import type { NumberValue } from 'd3-scale';
import type { ScaleLinear } from 'd3-scale';
import { ImpactHistoryEntry } from '../../types/careerCard';
import { IMPACT_LEVELS, ENGAGEMENT_LEVELS } from '../../constants/impactLevels';
import { 
  IoTrashOutline, 
  IoPencilOutline, 
  IoCheckmarkOutline, 
  IoCloseOutline,
  IoBarChartOutline,
  IoStatsChartOutline,
  IoListOutline,
  IoTrendingUpOutline,
  IoPeopleOutline
} from 'react-icons/io5';
import { useMediaQuery } from '@mui/material';
import { useUserTimezone } from '../../hooks/useUserTimezone';
import { useDarkMode } from '../../context/DarkModeContext';

interface TrendVisualizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  historyEntries: ImpactHistoryEntry[];
  title: string;
  onUpdateHistory?: (entries: ImpactHistoryEntry[]) => void;
}

interface TooltipData {
  entry: ImpactHistoryEntry;
  metricType: 'career_impact' | 'engagement_level';
}

// Responsive dimensions
const getChartDimensions = (isMobile: boolean) => ({
  width: isMobile ? window.innerWidth - 32 : 800,
  height: isMobile ? 300 : 500,
  margin: {
    top: 40,
    right: 40,
    bottom: isMobile ? 60 : 100,
    left: 70
  }
});

// Accessors
const getDate = (d: ImpactHistoryEntry) => new Date(d.timestamp);
const getCareerImpact = (d: ImpactHistoryEntry) => d.career_impact;
const getEngagementLevel = (d: ImpactHistoryEntry) => d.engagement_level;

// Smart time formatting with timezone support
const formatTimeByRange = (date: Date, range: number, timezone?: string) => {
  const MS_PER_DAY = 86400000;
  const MS_PER_MONTH = MS_PER_DAY * 30;
  const MS_PER_YEAR = MS_PER_DAY * 365;
  
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timezone
  };

  if (range < MS_PER_DAY) {
    options.hour = 'numeric';
    options.minute = '2-digit';
    options.hour12 = true;
    return date.toLocaleTimeString([], options);
  } else if (range < MS_PER_MONTH) {
    options.month = 'short';
    options.day = 'numeric';
    options.hour = 'numeric';
    options.minute = '2-digit';
    return date.toLocaleDateString([], options);
  } else if (range < MS_PER_YEAR) {
    options.month = 'short';
    options.day = 'numeric';
    return date.toLocaleDateString([], options);
  } else {
    options.month = 'short';
    options.year = 'numeric';
    return date.toLocaleDateString([], options);
  }
};

// Formatters
const formatAxisValue: TickFormatter<NumberValue> = (value) => String(value);

const ImpactAxis = ({ 
  scale, 
  left,
  label = "Impact Level",
  darkMode = false
}: { 
  scale: ScaleLinear<number, number>; 
  left: number;
  label?: string;
  darkMode?: boolean;
}) => (
  <AxisLeft
    scale={scale}
    left={left}
    tickValues={[1, 2, 3, 4, 5]}
    label={label}
    labelOffset={40}
    labelProps={{
      fontSize: 12,
      textAnchor: 'middle',
      fill: darkMode ? '#E5E7EB' : '#374151',
      transform: 'rotate(-90)'
    }}
    tickFormat={formatAxisValue}
    tickLabelProps={() => ({
      dx: '-0.5em',
      dy: '0.25em',
      fontSize: 11,
      textAnchor: 'end',
      fill: darkMode ? '#9CA3AF' : '#6B7280'
    })}
    stroke={darkMode ? '#4B5563' : '#E5E7EB'}
  />
);

export const TrendVisualizationModal: React.FC<TrendVisualizationModalProps> = ({
  isOpen,
  onClose,
  historyEntries,
  title,
  onUpdateHistory
}) => {
  const { darkMode } = useDarkMode();
  const isMobile = useMediaQuery('(max-width: 640px)');
  const dimensions = getChartDimensions(isMobile);
  const [viewMode, setViewMode] = useState<'timeline' | 'scatter' | 'table'>(isMobile ? 'table' : 'timeline');
  const [editingEntry, setEditingEntry] = useState<{ index: number; entry: ImpactHistoryEntry } | null>(null);
  const { formatWithTimezone, timezone } = useUserTimezone();
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip<TooltipData>();

  // Sort entries by date
  const sortedEntries = [...historyEntries].sort((a, b) => 
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  // Calculate time range and optimal number of ticks
  const timeRange = useMemo(() => {
    if (sortedEntries.length < 2) return null;
    const firstDate = new Date(sortedEntries[0].timestamp);
    const lastDate = new Date(sortedEntries[sortedEntries.length - 1].timestamp);
    const range = lastDate.getTime() - firstDate.getTime();
    return {
      start: firstDate,
      end: lastDate,
      range: range,
      tickCount: Math.min(
        Math.max(4, Math.floor((dimensions.width - dimensions.margin.left - dimensions.margin.right) / 150)),
        Math.ceil(range / (1000 * 60 * 60 * 24)) + 1
      )
    };
  }, [sortedEntries, dimensions]);

  // Create scales
  const timeScale = scaleTime({
    domain: [
      timeRange?.start || new Date(),
      timeRange?.end || new Date()
    ],
    range: [dimensions.margin.left, dimensions.width - dimensions.margin.right],
  });

  const impactScale = scaleLinear({
    domain: [1, 5],
    range: [dimensions.height - dimensions.margin.bottom, dimensions.margin.top],
  });

  const handleDeleteEntry = (index: number) => {
    if (!onUpdateHistory) return;
    const newEntries = [...sortedEntries];
    newEntries.splice(index, 1);
    onUpdateHistory(newEntries);
  };

  const handleEditEntry = (index: number) => {
    setEditingEntry({ index, entry: { ...sortedEntries[index] } });
  };

  const handleSaveEdit = () => {
    if (!editingEntry || !onUpdateHistory) return;

    const { entry } = editingEntry;
    if (!entry.timestamp || !entry.career_impact || !entry.engagement_level) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const otherEntries = sortedEntries.filter((_, idx) => idx !== editingEntry.index);
      const date = new Date(entry.timestamp);
      const timeZoneOffset = date.getTimezoneOffset();
      const offsetHours = Math.abs(Math.floor(timeZoneOffset / 60));
      const offsetMinutes = Math.abs(timeZoneOffset % 60);
      const offsetSign = timeZoneOffset <= 0 ? '+' : '-';
      const timestamp = `${date.toISOString().slice(0, 19)}${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;
      
      const updatedEntry = { ...entry, timestamp };
      const newEntries = [...otherEntries, updatedEntry];
      newEntries.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

      onUpdateHistory(newEntries);
      setEditingEntry(null);
    } catch (error) {
      console.error('Error updating entry:', error);
      alert('Failed to update entry. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
  };

  // Color schemes
  const colors = {
    careerImpact: darkMode ? '#FBBF24' : '#F59E0B',
    engagement: darkMode ? '#34D399' : '#10B981',
    grid: darkMode ? '#374151' : '#E5E7EB',
    text: darkMode ? '#E5E7EB' : '#1F2937',
    muted: darkMode ? '#9CA3AF' : '#6B7280',
    bg: darkMode ? '#1F2937' : '#FFFFFF',
    bgAlt: darkMode ? '#111827' : '#F9FAFB',
    border: darkMode ? '#374151' : '#E5E7EB'
  };

  const renderTimeline = () => (
    <svg width={dimensions.width} height={dimensions.height} className="overflow-visible">
      <defs>
        <linearGradient id="careerImpactGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={colors.careerImpact} stopOpacity={0.8} />
          <stop offset="100%" stopColor={colors.careerImpact} stopOpacity={0.1} />
        </linearGradient>
        <linearGradient id="engagementGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={colors.engagement} stopOpacity={0.8} />
          <stop offset="100%" stopColor={colors.engagement} stopOpacity={0.1} />
        </linearGradient>
      </defs>
      <Group>
        {/* Grid lines */}
        {[1, 2, 3, 4, 5].map(tick => (
          <line
            key={`horizontal-${tick}`}
            x1={dimensions.margin.left}
            x2={dimensions.width - dimensions.margin.right}
            y1={impactScale(tick)}
            y2={impactScale(tick)}
            stroke={colors.grid}
            strokeDasharray="4,4"
            opacity={0.5}
          />
        ))}
        
        {/* Area fills */}
        <LinePath
          data={sortedEntries}
          x={d => timeScale(getDate(d))}
          y={d => impactScale(getCareerImpact(d))}
          yScale={impactScale}
          stroke="transparent"
          fill="url(#careerImpactGradient)"
          curve={curveMonotoneX}
        />
        <LinePath
          data={sortedEntries}
          x={d => timeScale(getDate(d))}
          y={d => impactScale(getEngagementLevel(d))}
          yScale={impactScale}
          stroke="transparent"
          fill="url(#engagementGradient)"
          curve={curveMonotoneX}
        />
        
        {/* Lines */}
        <LinePath
          data={sortedEntries}
          x={d => timeScale(getDate(d))}
          y={d => impactScale(getCareerImpact(d))}
          stroke={colors.careerImpact}
          strokeWidth={3}
          curve={curveMonotoneX}
        />
        <LinePath
          data={sortedEntries}
          x={d => timeScale(getDate(d))}
          y={d => impactScale(getEngagementLevel(d))}
          stroke={colors.engagement}
          strokeWidth={3}
          curve={curveMonotoneX}
        />
        
        {/* Points with hover effect */}
        {sortedEntries.map((entry, i) => (
          <g key={`impact-${i}`}>
            <circle
              cx={timeScale(getDate(entry))}
              cy={impactScale(getCareerImpact(entry))}
              r={6}
              fill={colors.careerImpact}
              stroke={colors.bg}
              strokeWidth={3}
              className="cursor-pointer transition-all hover:r-8"
              onMouseEnter={(event) => {
                const coords = localPoint(event);
                if (coords) {
                  showTooltip({
                    tooltipData: { entry, metricType: 'career_impact' },
                    tooltipLeft: coords.x,
                    tooltipTop: coords.y
                  });
                }
              }}
              onMouseLeave={hideTooltip}
            />
          </g>
        ))}
        {sortedEntries.map((entry, i) => (
          <g key={`engagement-${i}`}>
            <circle
              cx={timeScale(getDate(entry))}
              cy={impactScale(getEngagementLevel(entry))}
              r={6}
              fill={colors.engagement}
              stroke={colors.bg}
              strokeWidth={3}
              className="cursor-pointer transition-all hover:r-8"
              onMouseEnter={(event) => {
                const coords = localPoint(event);
                if (coords) {
                  showTooltip({
                    tooltipData: { entry, metricType: 'engagement_level' },
                    tooltipLeft: coords.x,
                    tooltipTop: coords.y
                  });
                }
              }}
              onMouseLeave={hideTooltip}
            />
          </g>
        ))}
        
        {/* Axes */}
        <ImpactAxis scale={impactScale} left={dimensions.margin.left} darkMode={darkMode} />
        <AxisBottom
          scale={timeScale}
          top={dimensions.height - dimensions.margin.bottom}
          numTicks={timeRange?.tickCount}
          tickFormat={(value) => {
            const date = new Date(value as number);
            return timeRange ? formatTimeByRange(date, timeRange.range, timezone) : '';
          }}
          label="Time"
          labelOffset={50}
          labelProps={{
            fontSize: 12,
            textAnchor: 'middle',
            fill: colors.text
          }}
          tickLabelProps={() => ({
            angle: -30,
            textAnchor: 'end',
            fontSize: 11,
            dy: '0.8em',
            dx: '-0.5em',
            fill: colors.muted
          })}
          stroke={colors.grid}
        />
      </Group>
    </svg>
  );

  const renderScatterPlot = () => (
    <svg width={dimensions.width} height={dimensions.height} className="overflow-visible">
      <Group>
        {/* Grid lines */}
        {[1, 2, 3, 4, 5].map(tick => (
          <line
            key={`horizontal-${tick}`}
            x1={dimensions.margin.left}
            x2={dimensions.width - dimensions.margin.right}
            y1={impactScale(tick)}
            y2={impactScale(tick)}
            stroke={colors.grid}
            strokeDasharray="4,4"
            opacity={0.5}
          />
        ))}
        {[1, 2, 3, 4, 5].map(tick => (
          <line
            key={`vertical-${tick}`}
            x1={dimensions.margin.left + ((dimensions.width - dimensions.margin.left - dimensions.margin.right) * (tick - 1) / 4)}
            y1={dimensions.margin.top}
            x2={dimensions.margin.left + ((dimensions.width - dimensions.margin.left - dimensions.margin.right) * (tick - 1) / 4)}
            y2={dimensions.height - dimensions.margin.bottom}
            stroke={colors.grid}
            strokeDasharray="4,4"
            opacity={0.5}
          />
        ))}
        
        {/* Data points with gradient */}
        {sortedEntries.map((entry, i) => {
          const progress = i / (sortedEntries.length - 1);
          return (
            <circle
              key={i}
              cx={dimensions.margin.left + ((dimensions.width - dimensions.margin.left - dimensions.margin.right) * (entry.engagement_level - 1) / 4)}
              cy={impactScale(entry.career_impact)}
              r={8}
              fill={darkMode ? '#60A5FA' : '#3B82F6'}
              opacity={0.4 + progress * 0.6}
              stroke={colors.bg}
              strokeWidth={2}
              className="cursor-pointer transition-all hover:opacity-100"
              onMouseEnter={(event) => {
                const coords = localPoint(event);
                if (coords) {
                  showTooltip({
                    tooltipData: { entry, metricType: 'career_impact' },
                    tooltipLeft: coords.x,
                    tooltipTop: coords.y
                  });
                }
              }}
              onMouseLeave={hideTooltip}
            />
          );
        })}
        
        <ImpactAxis scale={impactScale} left={dimensions.margin.left} label="Career Impact" darkMode={darkMode} />
        <AxisBottom
          scale={scaleLinear({
            domain: [1, 5],
            range: [dimensions.margin.left, dimensions.width - dimensions.margin.right],
          })}
          top={dimensions.height - dimensions.margin.bottom}
          tickFormat={formatAxisValue}
          tickValues={[1, 2, 3, 4, 5]}
          label="Engagement Level"
          labelOffset={50}
          labelProps={{
            fontSize: 12,
            textAnchor: 'middle',
            fill: colors.text
          }}
          tickLabelProps={() => ({
            textAnchor: 'middle',
            fontSize: 11,
            dy: '1em',
            fill: colors.muted
          })}
          stroke={colors.grid}
        />
      </Group>
    </svg>
  );

  const renderDataTable = () => (
    <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Date
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Career Impact
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Engagement Level
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Notes
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {sortedEntries.map((entry, index) => (
              <motion.tr 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {formatWithTimezone(new Date(entry.timestamp))}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    entry.career_impact >= 4 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                    entry.career_impact >= 3 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {IMPACT_LEVELS[entry.career_impact as keyof typeof IMPACT_LEVELS].label}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    entry.engagement_level >= 4 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    entry.engagement_level >= 3 ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {ENGAGEMENT_LEVELS[entry.engagement_level as keyof typeof ENGAGEMENT_LEVELS].label}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                  {entry.note || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditEntry(index)}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    >
                      <IoPencilOutline className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteEntry(index)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                    >
                      <IoTrashOutline className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderViewModeSelector = () => (
    <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'flex-row gap-2'}`}>
      {[
        { mode: 'timeline' as const, icon: IoBarChartOutline, label: 'Timeline' },
        { mode: 'scatter' as const, icon: IoStatsChartOutline, label: 'Scatter' },
        { mode: 'table' as const, icon: IoListOutline, label: 'Table' }
      ].map(({ mode, icon: Icon, label }) => (
        <button
          key={mode}
          onClick={() => setViewMode(mode)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            viewMode === mode
              ? 'bg-blue-500 text-white shadow-lg scale-105'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          <Icon className="w-4 h-4" />
          {label}
        </button>
      ))}
    </div>
  );

  const renderMobileDataTable = () => (
    <div className="overflow-auto max-h-[calc(100vh-200px)] space-y-3">
      {sortedEntries.map((entry, index) => (
        <motion.div 
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4"
        >
          <div className="flex justify-between items-start mb-3">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {formatWithTimezone(new Date(entry.timestamp))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleEditEntry(index)}
                className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                aria-label="Edit entry"
              >
                <IoPencilOutline size={18} />
              </button>
              <button
                onClick={() => handleDeleteEntry(index)}
                className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                aria-label="Delete entry"
              >
                <IoTrashOutline size={18} />
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <IoTrendingUpOutline className="w-3 h-3" />
                Career Impact
              </div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                entry.career_impact >= 4 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                entry.career_impact >= 3 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
              }`}>
                {IMPACT_LEVELS[entry.career_impact as keyof typeof IMPACT_LEVELS].label}
              </span>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <IoPeopleOutline className="w-3 h-3" />
                Engagement
              </div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                entry.engagement_level >= 4 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                entry.engagement_level >= 3 ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
              }`}>
                {ENGAGEMENT_LEVELS[entry.engagement_level as keyof typeof ENGAGEMENT_LEVELS].label}
              </span>
            </div>
          </div>
          
          {entry.note && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-300">{entry.note}</div>
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );

  const renderEditForm = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
    >
      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
        Edit Entry
      </h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Date and Time
          </label>
          <input
            type="datetime-local"
            value={new Date(editingEntry!.entry.timestamp).toISOString().slice(0, 16)}
            onChange={(e) => setEditingEntry({
              ...editingEntry!,
              entry: { ...editingEntry!.entry, timestamp: new Date(e.target.value).toISOString() }
            })}
            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Career Impact
          </label>
          <select
            value={editingEntry!.entry.career_impact}
            onChange={(e) => setEditingEntry({
              ...editingEntry!,
              entry: { ...editingEntry!.entry, career_impact: Number(e.target.value) }
            })}
            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {Object.entries(IMPACT_LEVELS).map(([value, { label }]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Engagement Level
          </label>
          <select
            value={editingEntry!.entry.engagement_level}
            onChange={(e) => setEditingEntry({
              ...editingEntry!,
              entry: { ...editingEntry!.entry, engagement_level: Number(e.target.value) }
            })}
            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {Object.entries(ENGAGEMENT_LEVELS).map(([value, { label }]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Note (optional)
          </label>
          <input
            type="text"
            value={editingEntry!.entry.note || ''}
            onChange={(e) => setEditingEntry({
              ...editingEntry!,
              entry: { ...editingEntry!.entry, note: e.target.value }
            })}
            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Add a note..."
          />
        </div>
        
        <div className="flex gap-2 justify-end pt-4">
          <button
            onClick={handleCancelEdit}
            className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveEdit}
            className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors flex items-center gap-2"
          >
            <IoCheckmarkOutline className="w-4 h-4" />
            Save Changes
          </button>
        </div>
      </div>
    </motion.div>
  );

  // Legend component
  const ChartLegend = () => (
    <div className="absolute top-4 right-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-gray-200 dark:border-gray-700">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors.careerImpact }} />
          <span className="text-xs text-gray-700 dark:text-gray-300">Career Impact</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors.engagement }} />
          <span className="text-xs text-gray-700 dark:text-gray-300">Engagement Level</span>
        </div>
      </div>
    </div>
  );

  // Empty state
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
      <IoBarChartOutline className="w-16 h-16 mb-4 opacity-50" />
      <p className="text-lg font-medium">No data yet</p>
      <p className="text-sm">Start tracking your impact to see trends</p>
    </div>
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
        
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
          className={`relative ${
            isMobile ? 'w-full h-full' : 'w-[900px] max-h-[80vh]'
          } bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-4 text-white">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-6">
                <h2 className="text-lg font-semibold">{title}</h2>
                {sortedEntries.length > 0 && (
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="opacity-75">Latest Impact:</span>
                      <span className="font-medium">
                        {IMPACT_LEVELS[sortedEntries[sortedEntries.length - 1].career_impact as keyof typeof IMPACT_LEVELS].label}
                      </span>
                    </div>
                    <div className="w-px h-4 bg-white/30" />
                    <div className="flex items-center gap-2">
                      <span className="opacity-75">Latest Engagement:</span>
                      <span className="font-medium">
                        {ENGAGEMENT_LEVELS[sortedEntries[sortedEntries.length - 1].engagement_level as keyof typeof ENGAGEMENT_LEVELS].label}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Close modal"
              >
                <IoCloseOutline size={20} />
              </button>
            </div>
          </div>
          
          {/* Body */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800">
            <div className={`${isMobile ? 'space-y-4' : 'flex justify-between items-center'} mb-4`}>
              {renderViewModeSelector()}
            </div>
            
            <div className={`relative bg-white dark:bg-gray-900 rounded-lg p-4 shadow-sm ${
              isMobile ? 'h-[calc(100vh-280px)]' : 'h-[520px]'
            } overflow-auto`}>
              {sortedEntries.length === 0 ? (
                <EmptyState />
              ) : editingEntry && isMobile ? (
                renderEditForm()
              ) : (
                <>
                  {viewMode === 'timeline' && !isMobile && (
                    <div className="relative">
                      {renderTimeline()}
                      <ChartLegend />
                      {/* Tooltip for Timeline view */}
                      {tooltipOpen && tooltipData && (
                        <Tooltip
                          top={tooltipTop}
                          left={tooltipLeft}
                          className="!bg-white dark:!bg-gray-800 !text-gray-900 dark:!text-gray-100 !border !border-gray-200 dark:!border-gray-700 !shadow-xl !rounded-lg !p-3"
                          style={{
                            position: 'absolute',
                            pointerEvents: 'none',
                            transform: 'translate(-50%, -100%)',
                            marginTop: -10,
                            zIndex: 10
                          }}
                        >
                          <div className="space-y-2">
                            <div className="font-medium">
                              {formatWithTimezone(new Date(tooltipData.entry.timestamp))}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                tooltipData.metricType === 'career_impact' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              }`}>
                                {tooltipData.metricType === 'career_impact' 
                                  ? IMPACT_LEVELS[tooltipData.entry.career_impact as keyof typeof IMPACT_LEVELS]?.label
                                  : ENGAGEMENT_LEVELS[tooltipData.entry.engagement_level as keyof typeof ENGAGEMENT_LEVELS]?.label
                                }
                              </span>
                            </div>
                            {tooltipData.entry.note && (
                              <div className="text-sm text-gray-600 dark:text-gray-400 italic">
                                {tooltipData.entry.note}
                              </div>
                            )}
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  )}
                  {viewMode === 'scatter' && !isMobile && (
                    <div className="relative">
                      {renderScatterPlot()}
                      {/* Tooltip for Scatter view */}
                      {tooltipOpen && tooltipData && (
                        <Tooltip
                          top={tooltipTop}
                          left={tooltipLeft}
                          className="!bg-white dark:!bg-gray-800 !text-gray-900 dark:!text-gray-100 !border !border-gray-200 dark:!border-gray-700 !shadow-xl !rounded-lg !p-3"
                          style={{
                            position: 'absolute',
                            pointerEvents: 'none',
                            transform: 'translate(-50%, -100%)',
                            marginTop: -10,
                            zIndex: 10
                          }}
                        >
                          <div className="space-y-2">
                            <div className="font-medium">
                              {formatWithTimezone(new Date(tooltipData.entry.timestamp))}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                tooltipData.metricType === 'career_impact' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              }`}>
                                {tooltipData.metricType === 'career_impact' 
                                  ? IMPACT_LEVELS[tooltipData.entry.career_impact as keyof typeof IMPACT_LEVELS]?.label
                                  : ENGAGEMENT_LEVELS[tooltipData.entry.engagement_level as keyof typeof ENGAGEMENT_LEVELS]?.label
                                }
                              </span>
                            </div>
                            {tooltipData.entry.note && (
                              <div className="text-sm text-gray-600 dark:text-gray-400 italic">
                                {tooltipData.entry.note}
                              </div>
                            )}
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  )}
                  {(viewMode === 'table' || isMobile) && (
                    isMobile ? renderMobileDataTable() : renderDataTable()
                  )}
                </>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};